import { Text, TouchableOpacity, View } from "react-native";
import { Button } from "ui/gluestack/button";
import {
  UploadCloudIcon,
  UploadCloudTwoIcon,
} from "ui/gluestack/icon/UploadCloud";
import { AlertCircleIcon, Icon } from "ui/gluestack/icon";
import * as DocumentPicker from "expo-document-picker";
import { useDisplayToast } from "ui/hooks/useDisplayToast";
import { FieldError } from "react-hook-form";
import { generalAllowedExtensions } from "app/constants/allowedExtensionsConstants";
import { isDocumentPickerAsset, ThrelsFileType } from "ui/types/ThrelsFileType";
import { ViewDocument } from "app/components/atoms/ViewDocument";
import { validateAsset } from "ui/utils/fileUtils";

interface UploadDocumentAreaProps {
  value?: ThrelsFileType[];
  onChange: (files: ThrelsFileType[]) => void;
  allowedExtensions: string[];
  errorMessage?: FieldError;
  singleFile?: boolean;
}

export default function UploadDocumentArea({
  value = [],
  onChange,
  errorMessage,
  allowedExtensions = generalAllowedExtensions,
  singleFile,
}: UploadDocumentAreaProps) {
  const { showErrorToast } = useDisplayToast();

  const pickFile = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: allowedExtensions,
        copyToCacheDirectory: true,
        multiple: !singleFile,
      });

      if (!result.canceled) {
        const allowed = (allowedExtensions || []).map((e) => e.toLowerCase());

        const assets = result.assets || [];
        assets.forEach((a) => validateAsset(a, allowed));

        if (singleFile) {
          if (assets[0]) {
            onChange([assets[0]]);
          }
        } else {
          onChange([...value, ...assets]);
        }
      }
    } catch (error) {
      showErrorToast(error);
    }
  };

  const removeFile = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  return (
    <View>
      <Button
        action="secondary"
        className="bg-secondary-0 w-48 px-3 mt-3 mb-5"
        onPress={pickFile}
      >
        <Text>
          {singleFile ? "Upload relevant file" : "Upload relevant files"}
        </Text>
        <Icon as={UploadCloudIcon} className="w-5 h-5 text-primary-900" />
      </Button>

      {!value?.length ? (
        <TouchableOpacity onPress={pickFile}>
          <View className="w-[286px] h-[260px] mb-0  bg-background-50 border border-border-300 border-dashed rounded-xl justify-center items-center hover:bg-background-100">
            <Icon as={UploadCloudTwoIcon} className="w-11 h-11 text-gray-200" />
            <Text className="text-center text-gray-500">
              No files uploaded yet
            </Text>
          </View>
        </TouchableOpacity>
      ) : (
        <View className="flex flex-row flex-wrap gap-4">
          {value.map((file, index) => (
            <View key={index}>
              <ViewDocument document={file} />
              <TouchableOpacity onPress={() => removeFile(index)}>
                <Text className="text-primary-500 my-2.5 font-medium">
                  Remove
                </Text>
              </TouchableOpacity>
            </View>
          ))}
        </View>
      )}
      {errorMessage ? (
        <View className="flex-row gap-x-1 items-center mt-3">
          <Icon as={AlertCircleIcon} className="text-error-500" size="sm" />
          <Text className="text-error-500 text-sm">{errorMessage.message}</Text>
        </View>
      ) : null}
    </View>
  );
}
